/*
 * @Author: Ryan
 * @Date: 2025-12-26
 * @Description: Why choose FalcoCut AI Avatar Generator section with 6 feature cards
 */
import React from 'react';

interface FeatureCard {
  icon: string;
  title: string;
  description: string;
}

const features: FeatureCard[] = [
  {
    icon: '⚡',
    title: 'Fast & Scalable Production',
    description:
      'Generate avatar videos in minutes instead of days. Skip the filming, reshoots, and editing—just type your script or upload a photo, and FalcoCut does the rest. Perfect for scaling content quickly across campaigns or courses.',
  },
  {
    icon: '🎨',
    title: 'Customizable & On-Brand',
    description:
      'Design avatars that match your brand identity. Add logos, product placements, or branded outfits, and ensure every video looks consistent with your visual style. From corporate training to social media ads, your avatar stays on-brand.',
  },
  {
    icon: '🌐',
    title: "Speak Your Audience's Language",
    description:
      'Reach audiences worldwide with avatars that speak 30+ languages. Use AI voices or clone your own voice to maintain authenticity. Combined with lip sync, your avatar delivers messages naturally in any market.',
  },
  {
    icon: '👍',
    title: 'All-in-One Creation Platform',
    description:
      'No need for multiple tools. FalcoCut provides avatars, dubbing, subtitles, and editing in one workspace. Create, refine, and publish professional videos without technical skills or extra software.',
  },
  {
    icon: '✏️',
    title: 'One Script, Endless Variations',
    description:
      'Design avatars that match your brand identity. Add logos, product placements, or branded outfits, and ensure every video looks consistent with your visual style. From corporate training to social media ads, your avatar stays on-brand.',
  },
  {
    icon: '🧩',
    title: 'More than Talking Avatars',
    description:
      'FalcoCut offers a full set of supporting tools around avatars. Generate avatar images, turn them into videos, and enhance your content with built-in creation features. Everything works together to help you produce avatar content faster.',
  },
];

const FeatureCardItem: React.FC<FeatureCard> = ({ icon, title, description }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 shadow-sm border border-gray-100">
      <div className="text-3xl pad:text-4xl mb-3 pad:mb-4">{icon}</div>
      <h3 className="text-lg pad:text-xl font-semibold text-gray-900 mb-2 pad:mb-3">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

const WhyChooseSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Why Choose FalcoCut AI Avatar Generator
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-4xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          FalcoCut combines realism, speed, and flexibility in one platform. From creating
          digital twins and product presenters to multilingual talking avatars, our AI
          avatar generator gives you the tools to scale video production effortlessly.
        </p>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 gap-4 pad:gap-6">
          {features.map((feature, index) => (
            <FeatureCardItem
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
